import { useQuery } from '@tanstack/react-query'
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { adminApi, type TrainerAnalyticsDto } from '@/api/admin'
import { EmptyChart, SectionTitle, SortableTh } from './AnalyticsShared'
import { fmtRating, useSortable, CHART_COLORS, useAnalyticsMoney } from './analyticsHelpers'

export function TrainersTab({ from, to }: { from: string | null; to: string | null }) {
  const { fmtMoney, fmtMoneyShort } = useAnalyticsMoney()
  const { data = [], isLoading } = useQuery({
    queryKey: ['analytics-trainers', from, to],
    queryFn: () => adminApi.getTrainerAnalytics(from, to).then(r => r.data),
  })

  const { sorted, toggle, sortKey, sortDir } = useSortable<TrainerAnalyticsDto>(data, 'revenue')
  const hasLive = data.some(t => t.live)

  const chartData = [...data]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10)
    .map(t => ({
      name: t.trainerName.split(' ')[0],
      fullName: t.trainerName,
      revenue: t.revenue,
      rating: t.averageRating,
    }))

  if (isLoading) return <p className="text-gray-400 text-sm py-8">Loading…</p>

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>Revenue attributed (lead trainer)</SectionTitle>
          <div className="mt-4 h-56">
            {chartData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ bottom: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => [fmtMoney(Number(v)), 'Revenue']}
                    labelFormatter={(_, p) => p[0]?.payload?.fullName ?? ''}
                  />
                  <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>Average rating per trainer</SectionTitle>
          <div className="mt-4 h-56">
            {chartData.filter(d => d.rating != null).length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.filter(d => d.rating != null)} margin={{ bottom: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 10]} tickCount={6} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => [`${Number(v).toFixed(1)}/10`, 'Avg rating']}
                    labelFormatter={(_, p) => p[0]?.payload?.fullName ?? ''}
                  />
                  <Bar dataKey="rating" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <SortableTh col="trainerName"       sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Trainer</SortableTh>
              <SortableTh col="sessionsDelivered" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Sessions led</SortableTh>
              <SortableTh col="revenue"           sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Revenue</SortableTh>
              <SortableTh col="averageRating"     sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Avg rating</SortableTh>
              <SortableTh col="feedbackCount"     sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Reviews</SortableTh>
              <SortableTh col="certificatesIssued" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Certs issued</SortableTh>
              {hasLive && <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right whitespace-nowrap" title="Ended CertSlate Live sessions">Live sessions</th>}
              {hasLive && <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right whitespace-nowrap" title="Weighted by responses">Attendee feedback</th>}
              {hasLive && <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right whitespace-nowrap" title="Post-course vs pre-course, percentage points">Learning gain</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.length === 0 ? (
              <tr><td colSpan={hasLive ? 9 : 6} className="px-4 py-10 text-center text-gray-400 text-sm">No trainer data for this period</td></tr>
            ) : sorted.map(t => (
              <tr key={t.trainerId} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-gray-900">{t.trainerName}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{t.sessionsDelivered}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900">{fmtMoney(t.revenue)}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={t.averageRating != null ? (t.averageRating >= 8 ? 'text-green-600 font-medium' : t.averageRating >= 6 ? 'text-amber-600' : 'text-red-600') : 'text-gray-300'}>
                    {fmtRating(t.averageRating)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-500">{t.feedbackCount}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{t.certificatesIssued}</td>
                {hasLive && <td className="px-4 py-3 text-right tabular-nums text-gray-600">{t.live?.sessions ?? <span className="text-gray-300">—</span>}</td>}
                {hasLive && <td className="px-4 py-3 text-right tabular-nums text-gray-600">{t.live?.feedbackAverage != null ? `${t.live.feedbackAverage.toFixed(1)}/10` : <span className="text-gray-300">—</span>}</td>}
                {hasLive && <td className="px-4 py-3 text-right tabular-nums text-gray-600">{t.live?.learningGain != null ? `${t.live.learningGain >= 0 ? '+' : ''}${t.live.learningGain.toFixed(1)} pts` : <span className="text-gray-300">—</span>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
